import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts'
import type { AnalyticsBucket, DispatchAnalytics } from '@rapid-response/shared'
import { getDispatchAnalytics } from '~/server/analytics'
import { ActivityFeed } from '~/components/analytics/ActivityFeed'
import { EtaSourcesTab } from '~/components/analytics/EtaSourcesTab'
import { PageHeader } from '~/components/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~/components/ui/chart'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import {
  BUCKETS,
  COVERAGE_DEFAULTS,
  fmtAvg,
  fmtPercent,
  rangeFromPreset,
  RANGE_PRESETS,
} from '~/lib/analytics-format'

const ANALYTICS_TABS = ['activity', 'outcomes', 'coverage', 'eta-sources'] as const
type AnalyticsTab = (typeof ANALYTICS_TABS)[number]

const ACTIVITY_SUBTABS = ['dashboard', 'users', 'feed'] as const
type ActivitySubtab = (typeof ACTIVITY_SUBTABS)[number]

export const Route = createFileRoute('/analytics')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: AnalyticsTab; sub?: ActivitySubtab } => {
    const result: { tab?: AnalyticsTab; sub?: ActivitySubtab } = {}
    if (ANALYTICS_TABS.includes(search.tab as AnalyticsTab)) {
      result.tab = search.tab as AnalyticsTab
    }
    if (ACTIVITY_SUBTABS.includes(search.sub as ActivitySubtab)) {
      result.sub = search.sub as ActivitySubtab
    }
    return result
  },
  component: AnalyticsPage,
})

const DEFAULT_PRESET_DAYS = 30

function AnalyticsPage() {
  const [presetDays, setPresetDays] = useState<number>(DEFAULT_PRESET_DAYS)
  const [bucket, setBucket] = useState<AnalyticsBucket>('day')
  const [driveMins, setDriveMins] = useState<number>(COVERAGE_DEFAULTS.driveMins)
  const [freshMins, setFreshMins] = useState<number>(COVERAGE_DEFAULTS.freshMins)
  const [data, setData] = useState<DispatchAnalytics | null>(null)
  const [loading, setLoading] = useState(false)
  const { tab = 'activity', sub = 'dashboard' } = Route.useSearch()
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const { from, to } = rangeFromPreset(presetDays)
    getDispatchAnalytics({ data: { from, to, bucket, driveMins, freshMins } })
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((err) => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : 'Could not load analytics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [presetDays, bucket, driveMins, freshMins])

  const etaSources = tab === 'eta-sources'

  return (
    <div className="space-y-4">
      <PageHeader title="Analytics" description="Adoption and usage of the dispatch tool." />

      {!etaSources && (
        <div className="flex flex-wrap gap-2">
          {RANGE_PRESETS.map((p) => (
            <Button
              key={p.days}
              size="sm"
              variant={p.days === presetDays ? 'default' : 'outline'}
              onClick={() => setPresetDays(p.days)}
            >
              {p.label}
            </Button>
          ))}
          <div className="ml-auto flex gap-2">
            {BUCKETS.map((b) => (
              <Button
                key={b}
                size="sm"
                variant={b === bucket ? 'default' : 'outline'}
                onClick={() => setBucket(b)}
              >
                {b === 'day' ? 'Day' : 'Week'}
              </Button>
            ))}
          </div>
        </div>
      )}

      {!etaSources && data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Sessions" value={data.totals.sessions} />
          <StatCard label="Active users" value={data.totals.activeUsers} />
          <StatCard label="Assets searched" value={data.totals.assets} />
          <StatCard label="Acceptance rate" value={fmtPercent(data.totals.acceptanceRate)} />
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            to: '/analytics',
            search: (prev) => ({ tab: value as AnalyticsTab, sub: (prev as { sub?: ActivitySubtab }).sub }),
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="outcomes">Outcomes</TabsTrigger>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="eta-sources">ETA sources</TabsTrigger>
        </TabsList>

        {data ? (
          <>
            <TabsContent value="activity" className="pt-2">
              <ActivityTab
                data={data}
                sub={sub}
                onSubChange={(value) =>
                  navigate({
                    to: '/analytics',
                    search: (prev) => ({ tab: (prev as { tab?: AnalyticsTab }).tab, sub: value as ActivitySubtab }),
                    replace: true,
                  })
                }
              />
            </TabsContent>

            <TabsContent value="outcomes" className="pt-2 space-y-4">
              <AcceptanceFunnelCard funnel={data.funnel} />
              <TargetTable title="Officers" rows={data.officers} />
              <TargetTable title="Jobs" rows={data.jobs} />
            </TabsContent>

            <TabsContent value="coverage" className="pt-2">
              <CoverageSection
                coverage={data.coverage}
                driveMins={driveMins}
                setDriveMins={setDriveMins}
                freshMins={freshMins}
                setFreshMins={setFreshMins}
              />
            </TabsContent>
          </>
        ) : (
          !etaSources && <p className="text-sm text-muted-foreground">{loading ? 'Loading…' : 'No data.'}</p>
        )}

        <TabsContent value="eta-sources" className="pt-2">
          <EtaSourcesTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function ActivityTab({
  data,
  sub,
  onSubChange,
}: Readonly<{
  data: DispatchAnalytics
  sub: ActivitySubtab
  onSubChange: (value: string) => void
}>) {
  return (
    <Tabs value={sub} onValueChange={onSubChange}>
      <TabsList>
        <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
        <TabsTrigger value="users">Users</TabsTrigger>
        <TabsTrigger value="feed">Recent feed</TabsTrigger>
      </TabsList>

      <TabsContent value="dashboard" className="pt-2 space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <ActivityOverTimeCard activity={data.activity} />
          <DataUsageCard dataUsage={data.dataUsage} />
        </div>
        <ActivityFunnelCard funnel={data.activityFunnel} />
      </TabsContent>

      <TabsContent value="users" className="pt-2">
        <LeaderboardTable users={data.users} />
      </TabsContent>

      <TabsContent value="feed" className="pt-2">
        <ActivityFeed />
      </TabsContent>
    </Tabs>
  )
}

function StatCard({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="text-2xl font-semibold">{value}</CardContent>
    </Card>
  )
}

function ActivityOverTimeCard({ activity }: Readonly<{ activity: DispatchAnalytics['activity'] }>) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Activity over time</CardTitle></CardHeader>
      <CardContent>
        <ChartContainer config={{ sessions: { label: 'Sessions', color: 'var(--color-chart-1)' } }} className="h-56 w-full">
          <LineChart data={activity}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="bucket" tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} width={28} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Line type="monotone" dataKey="sessions" stroke="var(--color-sessions)" dot={false} />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function AcceptanceFunnelCard({ funnel }: Readonly<{ funnel: DispatchAnalytics['funnel'] }>) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Acceptance funnel</CardTitle></CardHeader>
      <CardContent>
        <ChartContainer config={{ value: { label: 'Count', color: 'var(--color-chart-2)' } }} className="h-56 w-full">
          <BarChart
            data={[
              { stage: 'Searches', value: funnel.sessionsRun },
              { stage: 'With outcome', value: funnel.sessionsWithOutcome },
              { stage: 'Accepted', value: funnel.outcomesAccepted },
              { stage: 'Accepted jobs', value: funnel.distinctAcceptedJobs },
            ]}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="stage" tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} width={28} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="value" fill="var(--color-value)" radius={4} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function DataUsageCard({ dataUsage }: Readonly<{ dataUsage: DispatchAnalytics['dataUsage'] }>) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Data usage (lookups)</CardTitle></CardHeader>
      <CardContent>
        <ChartContainer
          config={{ ok: { label: 'OK', color: 'var(--color-chart-1)' }, error: { label: 'Error', color: 'var(--color-chart-5)' } }}
          className="h-56 w-full"
        >
          <BarChart data={dataUsage}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="bucket" tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} width={28} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="ok" stackId="s" fill="var(--color-ok)" />
            <Bar dataKey="error" stackId="s" fill="var(--color-error)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function ActivityFunnelCard({
  funnel,
}: Readonly<{ funnel: DispatchAnalytics['activityFunnel'] }>) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Activity funnel (events)</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <StatCard label="Searches" value={funnel.searches} />
        <StatCard label="Officers shown" value={funnel.recommendationsOfficers} />
        <StatCard label="Jobs shown" value={funnel.recommendationsJobs} />
        <StatCard label="Called" value={funnel.called} />
        <StatCard label="Accepted" value={funnel.accepted} />
        <StatCard label="Declined" value={funnel.declined} />
        <StatCard label="Cleared" value={funnel.cleared} />
      </CardContent>
    </Card>
  )
}

function LeaderboardTable({ users }: Readonly<{ users: DispatchAnalytics['users'] }>) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Most active users</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr><th className="py-1">User</th><th>Sessions</th><th>Outcomes</th><th>Accepted</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.userId ?? 'none'} className="border-t">
                <td className="py-1.5">{u.name}</td>
                <td>{u.sessions}</td>
                <td>{u.outcomes}</td>
                <td>{u.accepted}</td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={4} className="py-2 text-muted-foreground">No activity in range.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function TargetTable({ title, rows }: Readonly<{ title: string; rows: DispatchAnalytics['officers'] }>) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{title} — outcomes by session</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr><th className="py-1">{title.slice(0, -1)}</th><th>Accepted</th><th>Declined</th><th>Called only</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.targetKey} className="border-t">
                <td className="py-1.5">{r.label}</td>
                <td>{r.accepted}</td>
                <td>{r.declined}</td>
                <td>{r.callAttempted}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="py-2 text-muted-foreground">No activity in range.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function CoverageSection({
  coverage,
  driveMins,
  setDriveMins,
  freshMins,
  setFreshMins,
}: Readonly<{
  coverage: DispatchAnalytics['coverage']
  driveMins: number
  setDriveMins: (n: number) => void
  freshMins: number
  setFreshMins: (n: number) => void
}>) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <h2 className="text-sm font-semibold">Coverage</h2>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="driveMins" className="text-xs text-muted-foreground">Drive ≤ (min)</Label>
          <Input id="driveMins" type="number" min={1} className="h-8 w-20" value={driveMins}
            onChange={(e) => setDriveMins(Math.max(1, Math.round(Number(e.target.value)) || COVERAGE_DEFAULTS.driveMins))} />
        </div>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="freshMins" className="text-xs text-muted-foreground">Fresh ≤ (min)</Label>
          <Input id="freshMins" type="number" min={1} className="h-8 w-20" value={freshMins}
            onChange={(e) => setFreshMins(Math.max(1, Math.round(Number(e.target.value)) || COVERAGE_DEFAULTS.freshMins))} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard label="Avg officers / search" value={fmtAvg(coverage.avgOfficers)} />
        <StatCard label={`Avg within ${coverage.driveMins}m drive`} value={fmtAvg(coverage.avgWithinDriveTime)} />
        <StatCard label={`Officer last ping (≤${coverage.freshMins}m)`} value={fmtAvg(coverage.avgFresh)} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">Coverage by outcode (thinnest first)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr><th className="py-1">Outcode</th><th>Searches</th><th>Avg officers</th><th>Avg within drive</th><th>Officer last ping</th></tr>
            </thead>
            <tbody>
              {coverage.byOutcode.map((r) => (
                <tr key={r.outcode} className="border-t">
                  <td className="py-1.5">{r.outcode}</td>
                  <td>{r.searches}</td>
                  <td>{fmtAvg(r.avgOfficers)}</td>
                  <td>{fmtAvg(r.avgWithinDriveTime)}</td>
                  <td>{fmtAvg(r.avgFresh)}</td>
                </tr>
              ))}
              {coverage.byOutcode.length === 0 && (
                <tr><td colSpan={5} className="py-2 text-muted-foreground">No operative lookups in range.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Coverage over time</CardTitle></CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              avgOfficers: { label: 'Avg officers', color: 'var(--color-chart-1)' },
              avgFresh: { label: 'Officer last ping', color: 'var(--color-chart-2)' },
            }}
            className="h-56 w-full"
          >
            <LineChart data={coverage.trend}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="bucket" tickLine={false} axisLine={false} />
              <YAxis allowDecimals width={28} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="avgOfficers" stroke="var(--color-avgOfficers)" dot={false} />
              <Line type="monotone" dataKey="avgFresh" stroke="var(--color-avgFresh)" dot={false} />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}
